import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import { z } from 'zod'
import { kUserBootstrapPath } from '@/constants/constants-api-paths'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

const UserBootstrapResponseSchema = z.object({
  success: z.literal(true),
})

export type UserBootstrapResponse = z.infer<typeof UserBootstrapResponseSchema>

function formatErrorDetail(data: unknown): string {
  const responseData = data as { detail?: unknown } | undefined
  const detail = responseData?.detail ?? data
  if (typeof detail === 'string') return detail
  if (detail == null) return ''
  return JSON.stringify(detail)
}

export async function postUserBootstrap(): Promise<UserBootstrapResponse> {
  const sentryTransactionId = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error(kErrorCodeAuthError)

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentryTransactionId,
        Authorization: `Bearer ${token}`,
      },
      method: 'POST',
    }

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kUserBootstrapPath}`
    const response = await fetch(apiUrl, requestOptions)
    const { data, parseError } = await readResponseJson(response)

    if (!response.ok) {
      const errorData = parseError ? {} : data
      const detail = formatErrorDetail(errorData)
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            responseData: errorData,
            parseError: parseError ? formatUnknownError(parseError) : null,
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiError(response.status, `Fetch error - status: ${response.status}, detail: ${detail}`, detail)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('User bootstrap response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            parseError: formatUnknownError(parseError),
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid user bootstrap response JSON')
    }

    const parsed = UserBootstrapResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('User bootstrap response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid user bootstrap response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled },
        })
      })
    }
    throw error
  }
}
